import React from 'react';

import { Checkbox, Divider, FormControlLabel, FormGroup, FormLabel, Input, MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import StyledFormTitle from '../styles/form-title';
import SaveButton from '../components/SaveButton';
import UnauthorizedUserAccessSetting from './UnauthorizedUserAccessSetting';
import Select from '../../../components/forms/Select';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';
import { ClassLevelOverrideOptions } from '../../../types/adminConfig';
import { IconTooltip } from '../../../components/common/Tooltip/IconTooltip';
import { StyledFormControl } from '../styles';

const StyledFormGroupContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 28px;
`;

const StyledFormGroup = styled(FormGroup)`
    margin-left: 15px;
`;

const StyledInput = styled(Input)`
    margin-right: 1rem;
`;

const StyledHelperText = styled(Typography)`
    color: ${(props) => props.theme.colors.text.secondary};
`;

const StyledSelect = styled(Select)`
    width: 400px;
`;

const StyledDiv = styled.div`
    margin-left: 10px;
`;

const AuthSettings = () => {
    const { adminSettings, setAdminSettings, isSavingSettingsData, handleSaveSettings } =
        useContainer(AdminSettingsContainer);
    const { isAdmin, isSuperAdmin } = useContainer(AdminAuthContainer);
    const { authSettings } = adminSettings;

    const { featureSettings } = adminSettings;
    const { options } = featureSettings;
    const { authenticationOverride = ClassLevelOverrideOptions.NoOne} = options;

    const { t } = useTranslation('admin_settings');

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id || event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    }

    const renderVanityUrls = () => {
        if (!isSuperAdmin) return null;

        return (
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <StyledInput
                                value={authSettings.vanityUrl}
                                onChange={handleChange}
                                id="vanityUrl"
                                name="vanityUrl"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('auth.vanityUrl') as string}
                    />
                </FormGroup>
            </StyledFormGroupContainer>
        );
    };

    const renderOwnedDomains = () => {
        if (!isSuperAdmin) return null;

        return (
            <StyledFormGroupContainer>
                <FormGroup>
                    <AdminControlCheckbox
                        control={
                            <StyledInput
                                value={authSettings.domains}
                                onChange={handleChange}
                                id="domains"
                                name="domains"
                                disabled={!isAdmin}
                            />
                        }
                        label={t('auth.domains') as string}
                    />
                </FormGroup>
            </StyledFormGroupContainer>
        );
    };

    return (
        <>
            <StyledFormTitle>{t('auth.title')}</StyledFormTitle>

            <StyledDiv>
                <UnauthorizedUserAccessSetting />
            </StyledDiv>

            <StyledFormGroupContainer>
                <StyledFormGroup sx={{ mb: 2 }}>
                    <FormLabel id="auth-override-label">{t('configuration.auth-override')}</FormLabel>
                    <RadioGroup
                        aria-labelledby="auth-override-label"
                        name="authenticationOverride"
                        value={authenticationOverride}
                        onChange={handleUpdateOptionsRadio}
                    >
                        <StyledFormControl>
                            <FormControlLabel value={ClassLevelOverrideOptions.NoOne} control={<Radio />} label={t('configuration.no-one')} />
                            <IconTooltip>{t('configuration.no-one-auth-access-tooltip')}</IconTooltip>
                        </StyledFormControl>
                        <FormControlLabel value={ClassLevelOverrideOptions.AdminsOnly} control=<Radio /> label={t('configuration.administrators')} />
                        <FormControlLabel value={ClassLevelOverrideOptions.AdminsAndInstructors} control=<Radio /> label={t('configuration.admins-and-instructors')} />
                    </RadioGroup>
                </StyledFormGroup>
                <Divider />
            </StyledFormGroupContainer>

            <StyledFormGroupContainer>
                <AdminControlCheckbox
                    control={
                        <Checkbox
                            checked={authSettings.forcedAuthentication}
                            onChange={handleCheckboxChange}
                            id="forcedAuthentication"
                            name="forcedAuthentication"
                        />
                    }
                    label={t('auth.forcedAuthenticationControl') as string}
                />
                <StyledHelperText variant="body2">{t('auth.helperTextLine1')}</StyledHelperText>
            </StyledFormGroupContainer>

            <StyledFormGroupContainer>
                <FormGroup>
                    <StyledSelect
                        name="autoVerify"
                        id="autoVerify"
                        value={authSettings.autoVerify}
                        label={t('auth.autoVerify') as string}
                        labelId="select-auto-verify"
                        onChange={handleChange}
                    >
                        <MenuItem value={'never'}>{t('auth.autoVerifyNever') as string}</MenuItem>
                        <MenuItem value={'optional'}>{t('auth.autoVerifyOptional') as string}</MenuItem>
                        <MenuItem value={'always'}>{t('auth.autoVerifyAlways') as string}</MenuItem>
                    </StyledSelect>
                </FormGroup>
            </StyledFormGroupContainer>

            {renderVanityUrls()}
            {renderOwnedDomains()}

            <StyledFormGroupContainer>
                <SaveButton isSaving={isSavingSettingsData} onClick={handleSaveSettings} />
            </StyledFormGroupContainer>
        </>
    );
};

export default AuthSettings;
